import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { adminGetFeedbacks, adminDeleteFeedback } from "../../../services/feedback";
import { formatDateToCustomString } from "../../../utils/utils";
import Pagination from "../../../components/Ui/Paging";
import { calculatePage } from "../../../utils/paging";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";

const USER_TYPE_COLORS = {
  paid: "bg-green-100 text-green-800",
  free: "bg-gray-100 text-gray-600",
};

const SHARE_BASE = process.env.REACT_APP_DOMAIN || "https://stage.gospeltruth.ai";

const shareLink = (messageId) => `${SHARE_BASE}/share/${messageId}`;

export default function AdminFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("");
  const [selected, setSelected] = useState(null); // detail modal
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminGetFeedbacks({ page, limit: paging.limit, search, userType });
      setFeedbacks(res.data.data || []);
      setPaging((p) => ({
        ...p,
        page,
        total: res.data.total,
        totalPages: calculatePage(res.data.total, p.limit),
      }));
    } catch {
      toast.error("Failed to load feedbacks");
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, userType, paging.limit]);

  useEffect(() => { fetch(1); }, [fetch]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    setDeleting(id);
    try {
      await adminDeleteFeedback(id);
      toast.success("Deleted");
      setFeedbacks((prev) => prev.filter((f) => f._id !== id));
      if (selected?._id === id) setSelected(null);
      setPaging((p) => ({ ...p, total: p.total - 1 }));
    } catch {
      toast.error("Failed to delete");
    }
    setDeleting(null);
  };

  const truncate = (str, n = 80) =>
    str && str.length > n ? str.slice(0, n) + "…" : str;

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex w-full justify-between items-center p-3 flex-wrap gap-3">
        <h2 className="font-semibold text-3xl text-gray-900">
          Feedback
          <span className="ml-3 text-base font-normal text-gray-400">({paging.total} total)</span>
        </h2>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="">All users</option>
            <option value="paid">Paid</option>
            <option value="free">Free</option>
          </select>
          <input
            type="text"
            placeholder="Search email, name, feedback…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetch(1)}
            className="border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button
            onClick={() => fetch(1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Question</th>
              <th className="px-4 py-3">Feedback</th>
              <th className="px-4 py-3">Chat Link</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {feedbacks.map((item, i) => (
              <tr
                key={item._id}
                className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelected(item)}
              >
                <td className="px-4 py-3">{(paging.page - 1) * paging.limit + i + 1}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{item.userName || "—"}</div>
                  <div className="text-xs text-gray-400">{item.userEmail || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${USER_TYPE_COLORS[item.userType] || "bg-gray-100 text-gray-500"}`}>
                    {item.userType}
                  </span>
                </td>
                <td className="px-4 py-3 max-w-[180px]">
                  <span className="text-gray-700">{truncate(item.question, 60)}</span>
                </td>
                <td className="px-4 py-3 max-w-[220px]">
                  <span className="text-gray-700">{truncate(item.feedbackText, 80)}</span>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={shareLink(item.messageId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 text-xs font-medium underline"
                  >
                    View chat ↗
                  </a>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">
                  {formatDateToCustomString(item.createdAt)}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleDelete(item._id)}
                    disabled={deleting === item._id}
                    className="text-red-500 hover:text-red-700 text-xs font-medium disabled:opacity-50"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {feedbacks.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-gray-400">
                  No feedback yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {paging.totalPages > 1 && (
        <Pagination
          totalPages={paging.totalPages}
          currentPage={paging.page}
          onPageChange={fetch}
        />
      )}

      {loading && <LoadingOverlay />}

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Feedback Detail</h3>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-5">
              {/* User info */}
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="font-semibold text-gray-800">{selected.userName || "Unknown"}</span>
                  <span className="text-sm text-gray-500">{selected.userEmail}</span>
                </div>
                <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${USER_TYPE_COLORS[selected.userType]}`}>
                  {selected.userType === "paid" ? "Paid User" : "Free User"}
                </span>
              </div>

              <div className="text-xs text-gray-400">
                Submitted: {formatDateToCustomString(selected.createdAt)}
              </div>

              {/* Chat Link */}
              {selected.messageId && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Chat Link</span>
                  <a
                    href={shareLink(selected.messageId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                  >
                    {shareLink(selected.messageId)}
                  </a>
                </div>
              )}

              {/* Question */}
              {selected.question && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">User Question</p>
                  <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {selected.question}
                  </div>
                </div>
              )}

              {/* AI Response */}
              {selected.aiResponse && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">AI Response</p>
                  <div className="bg-blue-50 rounded-lg px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selected.aiResponse}
                  </div>
                </div>
              )}

              {/* Feedback */}
              <div>
                <p className="text-xs font-semibold text-yellow-600 uppercase mb-1">Feedback</p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-gray-800 whitespace-pre-wrap">
                  {selected.feedbackText}
                </div>
              </div>

              {/* Delete button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => handleDelete(selected._id)}
                  disabled={deleting === selected._id}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {deleting === selected._id ? "Deleting…" : "Delete Feedback"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
